import * as ko from 'knockout';
import Sortable from 'sortablejs';
import { Modal } from 'bootstrap';
import LZString from 'lz-string';
import toastr from 'toastr';
import { SelectionType } from './enums/selection-type';
import type { IWeek } from './interfaces/IWeek';
import type { IGame } from './interfaces/IGame';
import type { ITeam } from './interfaces/ITeam';
import {
  fromLeagueEngineWatchlist,
  toLeagueEngineWatchlist,
  Watchlist,
} from './watchlist';
import type { ILeagueEngineWatchlistData } from './watchlist';
import { GameType } from './models/game-type';
import { Enumerable } from './linq';
import type { AppConfig } from './config';
import { getApimBaseUrl } from './config';
import { show as showSpinner, hide as hideSpinner } from './spinner';
import { ppFetch } from './pp-fetch';
import { apiFetch } from './apim-fetch';
import { userConfigManager } from './user-config-manager';
import { schedulePickPutDebounced } from './pending-picks';

// ─── Types ─────────────────────────────────────────────────────────────────

type TeamObs = KnockoutObservable<ITeam>;

// ─── Toaster helpers ────────────────────────────────────────────────────────

const TOASTR_OPTIONS: ToastrOptions = {
  closeButton: true,
  debug: false,
  newestOnTop: true,
  progressBar: false,
  positionClass: 'toast-top-center',
  preventDuplicates: false,
  onclick: null,
  showDuration: 300,
  hideDuration: 1000,
  timeOut: 5000,
  extendedTimeOut: 1000,
  showEasing: 'swing',
  hideEasing: 'linear',
  showMethod: 'fadeIn',
  hideMethod: 'fadeOut',
};

function toastSuccess(message: string): void {
  toastr.options = TOASTR_OPTIONS;
  toastr.success(message, 'Success');
}

function toastWarning(message: string): void {
  toastr.options = TOASTR_OPTIONS;
  toastr.warning(message, 'Warning');
}

function toastError(message: string): void {
  toastr.options = TOASTR_OPTIONS;
  toastr.error(message, 'Error');
}

// ─── Modal helper ─────────────────────────────────────────────────────────────
// Replaces the jQuery/bootbox-based PlayoffPredictor.Modal.Load.

const WATCHLIST_MODAL_HTML = `
  <div class="row" data-bind="with:SelectedWatchlist">
    <div class="col-12">
      <div class="mb-3">
        <label for="WatchlistName" class="col-4 form-label is-required">Scenario Name:</label>
        <input class="form-control col-8" type="text" id="WatchlistName" maxlength="200" data-bind="value:WatchlistName" />
      </div>
    </div>
    <div class="col-12">
      <div class="mb-3">
        <label for="Description" class="col-4 form-label">Description:</label>
        <textarea class="form-control col-8" type="text" id="Description" data-bind="value:Description"></textarea>
      </div>
    </div>
  </div>
`;

function openModal(
  html: string,
  modalSettings: Pick<ModalSettings, 'title' | 'buttons'>,
): HTMLElement {
  const buttons = modalSettings.buttons ?? {};
  const buttonsHtml = Object.entries(buttons).map(([key, btn]: [string, any]) => {
    const variant = key === 'save' ? 'primary' : 'secondary';
    const label = btn?.label ?? key;
    return `<button type="button" class="btn btn-${variant}" data-modal-action="${key}">${label}</button>`;
  }).join('');

  const modalEl = document.createElement('div');
  modalEl.className = 'modal fade';
  modalEl.setAttribute('tabindex', '-1');
  modalEl.innerHTML = `
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">${modalSettings.title ?? ''}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">${html}</div>
        <div class="modal-footer">${buttonsHtml}</div>
      </div>
    </div>
  `;
  document.body.appendChild(modalEl);

  const bsModal = new Modal(modalEl);

  // Wire footer buttons → callback. Hide modal when callback returns anything but false.
  modalEl.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-modal-action]') as HTMLElement | null;
    if (!btn) {
      return;
    }
    const action = btn.getAttribute('data-modal-action');
    const handler = (buttons as any)[action!];
    if (handler?.callback) {
      const result = handler.callback();
      if (result !== false) {
        bsModal.hide();
      }
    } else {
      bsModal.hide();
    }
  });

  // Clean up the DOM element when Bootstrap hides the modal.
  modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove());

  bsModal.show();

  return modalEl;
}

// ─── SchedulePickerBase ──────────────────────────────────────────────────────

export class SchedulePickerBase {
  // Public KO properties
  MaxWeek: KnockoutObservable<number>;
  AllTeams: KnockoutObservableArray<TeamObs>;
  GameScheduleGameMap: GameScheduleMap;
  Weeks: WeekDictionary;
  SelectedWeek: KnockoutObservable<ISelectedWeek | null>;
  SelectedTeam: KnockoutObservable<ITeam>;
  LeagueName: KnockoutObservable<string>;
  SaveVal: KnockoutComputed<string | undefined>;
  SaveString: KnockoutObservable<string>;
  FakeObs: KnockoutObservable<number | undefined>;
  BlockHistoryPush: KnockoutObservable<boolean>;
  UrlString: KnockoutObservable<string>;
  IsTeamSelected: KnockoutObservable<boolean>;
  CurrentSeasonString: KnockoutObservable<string>;
  BiggestWeek: KnockoutObservable<number>;
  EmptySeasonString: string;
  NumberOfTeams: KnockoutObservable<number>;
  UrlRoute: KnockoutObservable<string>;
  GameArray: number[];
  IsEditing: KnockoutObservable<boolean>;
  Watchlists: KnockoutObservableArray<KnockoutObservable<Watchlist>>;
  SelectedWatchlist: KnockoutObservable<Watchlist>;
  ShowSettings: KnockoutObservable<boolean>;
  Hash: KnockoutObservable<string>;
  PredictorSport: string;
  TotalWeeks: number;
  IsCustomLeague: KnockoutObservable<boolean>;
  IsAfterFirstLoad: boolean = false;
  LoadedWatchlist: KnockoutObservable<Watchlist>;
  IsComputing: KnockoutObservable<boolean>;

  /** PrimeTime game types — populated by the sport-specific subclass. */
  protected PrimeTimeList: KnockoutObservableArray<GameType>;

  /** Expose SelectionType enum for use in cshtml data-bind expressions ($root.SelectionType.HOME_WIN). */
  public SelectionType = SelectionType;

  /** Nightmode computed — exposes body class state for cshtml ($root.isNightmode()). */
  public isNightmode: KnockoutComputed<boolean>;

  // Config passed in from the subclass entry point
  protected config: AppConfig;

  // Compress callback timer
  compressCallback: ReturnType<typeof setTimeout> | undefined;

  constructor(predictorSport: string, config: AppConfig) {
    const self = this;

    self.config = config;
    showSpinner();

    if (!self.Hash) {
      self.Hash = ko.observable('');
    }

    self.PredictorSport = predictorSport;
    self.GameArray = [];
    self.IsComputing = ko.observable(false);
    self.MaxWeek = ko.observable(0);
    self.IsEditing = ko.observable(false);
    self.Watchlists = ko.observableArray<KnockoutObservable<Watchlist>>([]);
    self.LoadedWatchlist = ko.observable(null as unknown as Watchlist);
    self.SelectedWatchlist = ko.observable(null as unknown as Watchlist);
    self.ShowSettings = ko.observable(false);
    self.BiggestWeek = ko.observable(1);
    self.LeagueName = ko.observable('');
    self.BlockHistoryPush = ko.observable<boolean>(false);
    self.UrlString = ko.observable<string>('');
    self.IsTeamSelected = ko.observable<boolean>(false);
    self.CurrentSeasonString = ko.observable('');
    self.IsCustomLeague = ko.observable(false);
    self.SelectedTeam = ko.observable<ITeam>(null as unknown as ITeam);
    self.SelectedWeek = ko.observable<ISelectedWeek | null>(null);
    self.UrlRoute = ko.observable('');
    self.NumberOfTeams = ko.observable(0);
    self.AllTeams = ko.observableArray<TeamObs>([]);
    self.GameScheduleGameMap = {};
    self.PrimeTimeList = ko.observableArray<GameType>([]);

    self.isNightmode = ko.computed(() => document.body.classList.contains('nightmode'));

    // ── Watchlist: load single watchlist by wlId query param ───────────────
    const wlId = SchedulePickerBase.getQueryVariable('wlId');
    if (wlId) {
      self.loadWatchlistFromLeagueEngine(wlId)
        .then((watchlist) => {
          if (watchlist) {
            self.LoadedWatchlist(watchlist);
          }
        })
        .catch(() => toastError('Unable to load this saved scenario'));
    }

    self.SelectedWeek.subscribe((newVal: ISelectedWeek | null) => {
      if (newVal && newVal.key) {
        // window.ga("send", "event", "Week", `Changed to ${newVal.key}`);
      }
    });

    self.FakeObs = ko.observable<number | undefined>(undefined);
    self.SaveString = ko.observable('');

    self.SaveVal = ko.pureComputed(() => {
      if (self.config.draftOrderPage) {
        return undefined;
      }
      self.FakeObs();
      self.SaveString();
      return window.location.origin + '/?L=' + self.SaveString();
    });

    // ── Browser history (back/forward) ─────────────────────────────────────
    window.onpopstate = (event: PopStateEvent) => {
      if (self.config.draftOrderPage) {
        return;
      }
      if (event.state) {
        self.BlockHistoryPush(true);
        const newString: string = event.state.path.split('=')[1];
        showSpinner();
        setTimeout(() => {
          self.UrlString(newString);
          self.UrlString.valueHasMutated();
          if (event.state.path.indexOf('#') > -1) {
            const selectedTeamAbbreviation: string = event.state.path.split('#')[1];
            if ((self as unknown as Record<string, Function>)['showTeamStats']) {
              (self as unknown as Record<string, Function>)['showTeamStats'](
                self.findTeam(selectedTeamAbbreviation)(),
              );
            }
          } else {
            self.IsTeamSelected(false);
          }
          self.BlockHistoryPush(false);
        }, 50);
      }
    };

    // ── SelectedTeam subscription: push hash to history ───────────────────
    self.SelectedTeam.subscribe((newVal: ITeam) => {
      if (self.config.draftOrderPage) {
        return;
      }
      if (history.state) {
        let baseUrl: string;
        if (history.state.path.indexOf('#') > -1) {
          baseUrl = history.state.path.split('#')[0];
        } else {
          baseUrl = history.state.path;
        }
        if (!newVal.ProgramName) {
          const newurl = baseUrl + '#' + newVal.TeamAbbreviation;
          window.history.pushState({ path: newurl }, '', newurl);
        } else {
          const newurl = baseUrl + `#Team_${newVal.TeamID}`;
          window.history.pushState({ path: newurl }, '', newurl);
        }
      }
    });

    // ── IsTeamSelected subscription: remove hash from history ─────────────
    self.IsTeamSelected.subscribe((newVal: boolean) => {
      if (self.config.draftOrderPage) {
        return;
      }
      if (newVal === false) {
        if (history.state) {
          let baseUrl: string;
          if (history.state.path.indexOf('#') > -1) {
            baseUrl = history.state.path.split('#')[0];
          } else {
            baseUrl = history.state.path;
          }
          window.history.pushState({ path: baseUrl }, '', baseUrl);
        }
      }
    });

    // ── Load watchlists from LeagueEngine ──────────────────────────────────
    if (self.config.isAuthenticated === true) {
      apiFetch<ILeagueEngineWatchlistData[] | undefined>({
        url: self.getWatchlistApiUrl(`watchlists/${self.getWatchlistSportId()}`),
        method: 'GET',
      }).then((watchlists) => {
        const tmpWatchlist: KnockoutObservable<Watchlist>[] = [];
        ko.utils.arrayForEach(watchlists ?? [], (watchlist: ILeagueEngineWatchlistData) => {
          tmpWatchlist.push(ko.observable(new Watchlist(fromLeagueEngineWatchlist(watchlist))));
        });
        self.Watchlists(
          Enumerable.From(tmpWatchlist)
            .OrderBy((p: KnockoutObservable<Watchlist>) => p().SortOrder())
            .ToArray(),
        );

        // Wire up sortable for watchlist reordering
        const watchlistTbody = document.querySelector<HTMLElement>('#watchlist table tbody');
        if (watchlistTbody) {
          Sortable.create(watchlistTbody, {
            handle: '.js-handle',
            onEnd: () => {
              watchlistTbody.querySelectorAll<HTMLElement>('tr').forEach((tr, idx) => {
                ko.contextFor(tr).$data.SortOrder(idx + 1);
              });
              apiFetch<void>({
                url: self.getWatchlistApiUrl(`watchlist/${self.getWatchlistSportId()}`),
                method: 'PUT',
                data: self.Watchlists().map((item: KnockoutObservable<Watchlist>) => ({
                  id: item().WatchlistGUID,
                  idx: item().SortOrder(),
                })),
              })
                .then(() => toastSuccess('Watchlist order has been updated'))
                .catch(() => toastError('Unable to update your saved scenario order'));
            },
          });
        }
      }).catch(() => toastError('Unable to load your saved scenarios'));
    }

    // ── Body click handler: deselect team when clicking outside ───────────
    document.body.addEventListener('click', (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.className !== 'teamname' &&
        target.className !== 'interactable' &&
        target.nodeName !== 'IMG' &&
        target.id !== 'nightmode' &&
        target.className !== 'team-rank'
      ) {
        self.IsTeamSelected(false);
      }
    });

    const teamBreakoutAndTd = document.querySelectorAll<HTMLElement>('#team-breakout, td');
    teamBreakoutAndTd.forEach((el) => {
      el.addEventListener('click', (e: MouseEvent) => {
        e.stopPropagation();
      });
    });
  }

  // ─── Team helpers ──────────────────────────────────────────────────────────

  initTeamSubscribers(): void {
    for (const team of this.AllTeams()) {
      team().initSubscribers();
    }
  }

  findTeamByID(teamId: number): TeamObs {
    return ko.utils.arrayFirst(this.AllTeams(), (team: TeamObs) => team().TeamID === teamId);
  }

  findTeam(teamShortName: string): TeamObs {
    const teamId =
      teamShortName.indexOf('_') !== -1 ? parseInt(teamShortName.split('_')[1], 10) : -1;
    return ko.utils.arrayFirst(this.AllTeams(), (team: TeamObs) => {
      if (teamId !== -1) {
        return team().TeamID === teamId;
      }
      return team().TeamAbbreviation.toUpperCase() === teamShortName.toUpperCase();
    });
  }

  rankByConferenceRank(left: TeamObs, right: TeamObs): number {
    return left().ConferenceRank() - right().ConferenceRank();
  }

  // ─── Week navigation ───────────────────────────────────────────────────────

  previousWeek(): void {
    const nextWeekNum = parseInt(String(this.SelectedWeek().key), 10) - 1;
    if (nextWeekNum >= 1) {
      this.SelectedWeek({ key: nextWeekNum, value: this.Weeks[nextWeekNum] });
    }
    this.IsTeamSelected(false);
  }

  nextWeek(): void {
    const nextWeekNum = parseInt(String(this.SelectedWeek().key), 10) + 1;
    if (nextWeekNum <= this.TotalWeeks) {
      this.SelectedWeek({ key: nextWeekNum, value: this.Weeks[nextWeekNum] });
    }
    this.IsTeamSelected(false);
  }

  showSettings(showOrHide: boolean): void {
    this.ShowSettings(showOrHide);
  }

  switchContext(data: ISelectedWeek): void {
    this.SelectedWeek(data);
    this.IsTeamSelected(false);
  }

  // ─── Reset ────────────────────────────────────────────────────────────────

  reset(): boolean | void {
    const self = this;
    this.startComputing();
    if (confirm('Are you sure you want to reset your selections?')) {
      for (const weekInd in self.Weeks) {
        const gameType = self.Weeks[weekInd];
        ko.utils.arrayForEach(gameType, (week: IWeek) => {
          // Regular-season-style games live in the flat slot.games array
          // surfaced by GamesComputed.
          ko.utils.arrayForEach(week.GamesComputed(), (game: KnockoutObservable<IGame>) => {
            game().selectionType(SelectionType.NONE);
            game().selectionMade(false);
            game().teamSelected('');
          });
          // Series-style games (NHL/NBA/MLB best-of-N) live inside
          // week.Series[i].games — NOT in week.games — so they're invisible
          // to GamesComputed. Iterate them explicitly so reset() actually
          // clears playoff slots too. Empty no-op for sports that don't use
          // Series (NFL single-elim).
          ko.utils.arrayForEach(week.Series(), (series: ISeries) => {
            ko.utils.arrayForEach(series.games(), (game: KnockoutObservable<IGame>) => {
              game().selectionType(SelectionType.NONE);
              game().selectionMade(false);
              game().teamSelected('');
            });
          });
        });
      }

      for (let i = 0; i < self.AllTeams().length; i++) {
        const team = self.AllTeams()[i]();
        team.reset();
        team.FakeObs();
      }

      self.UrlString(self.EmptySeasonString);
      self.CurrentSeasonString(self.EmptySeasonString);
      self.SaveString(self.EmptySeasonString);

      const isDebugMode = userConfigManager.UserConfig.IsDebugMode;
      const newurl =
        window.location.protocol +
        '//' +
        window.location.host +
        window.location.pathname +
        '?L=' +
        self.EmptySeasonString +
        (isDebugMode ? '&dfpkey=testads' : '');
      window.history.pushState({ path: newurl }, '', newurl);

      self.pushHistory();

      for (let i = 0; i < self.AllTeams().length; i++) {
        const team = self.AllTeams()[i]();
        team.initSubscribers();
      }
      return true;
    }
    this.finishComputing();
  }

  // ─── Pct / record helpers ─────────────────────────────────────────────────

  BestPct(teams: TeamObs[]): TeamObs[] {
    return Enumerable.From(teams)
      .OrderByDescending((p: TeamObs) => p().GameRecords.AllRecord.Pct())
      .GroupBy((p: TeamObs) => p().GameRecords.AllRecord.Pct())
      .ToArray()[0].source;
  }

  BestPctNaN(teams: TeamObs[]): TeamObs[] {
    return Enumerable.From(teams)
      .OrderByDescending((p: TeamObs) => p().GameRecords.AllRecord.PctNaN())
      .GroupBy((p: TeamObs) => p().GameRecords.AllRecord.PctNaN())
      .ToArray()[0].source;
  }

  WorstPctNaN(teams: TeamObs[]): TeamObs[] {
    return Enumerable.From(teams)
      .OrderBy((p: TeamObs) => p().GameRecords.AllRecord.PctNaN())
      .GroupBy((p: TeamObs) => p().GameRecords.AllRecord.PctNaN())
      .ToArray()[0].source;
  }

  BestDivPct(teams: TeamObs[]): TeamObs[] {
    return Enumerable.From(teams)
      .OrderByDescending((p: TeamObs) => p().GameRecords.DivisionRecord.Pct())
      .GroupBy((p: TeamObs) => p().GameRecords.DivisionRecord.Pct())
      .ToArray()[0].source;
  }

  BestDivPctNaN(teams: TeamObs[]): TeamObs[] {
    return Enumerable.From(teams)
      .OrderByDescending((p: TeamObs) => p().GameRecords.DivisionRecord.PctNaN())
      .GroupBy((p: TeamObs) => p().GameRecords.DivisionRecord.PctNaN())
      .ToArray()[0].source;
  }

  WorstDivPctNaN(teams: TeamObs[]): TeamObs[] {
    return Enumerable.From(teams)
      .OrderBy((p: TeamObs) => p().GameRecords.DivisionRecord.PctNaN())
      .GroupBy((p: TeamObs) => p().GameRecords.DivisionRecord.PctNaN())
      .ToArray()[0].source;
  }

  BestConfPct(teams: TeamObs[]): TeamObs[] {
    return Enumerable.From(teams)
      .OrderByDescending((p: TeamObs) => p().GameRecords.ConferenceRecord.Pct())
      .GroupBy((p: TeamObs) => p().GameRecords.ConferenceRecord.Pct())
      .ToArray()[0].source;
  }

  BestConfPctNaN(teams: TeamObs[]): TeamObs[] {
    return Enumerable.From(teams)
      .OrderByDescending((p: TeamObs) => p().GameRecords.ConferenceRecord.PctNaN())
      .GroupBy((p: TeamObs) => p().GameRecords.ConferenceRecord.PctNaN())
      .ToArray()[0].source;
  }

  WorstConfPctNaN(teams: TeamObs[]): TeamObs[] {
    return Enumerable.From(teams)
      .OrderBy((p: TeamObs) => p().GameRecords.ConferenceRecord.PctNaN())
      .GroupBy((p: TeamObs) => p().GameRecords.ConferenceRecord.PctNaN())
      .ToArray()[0].source;
  }

  BestSOV(teams: TeamObs[]): TeamObs[] {
    return Enumerable.From(teams)
      .OrderByDescending((p: TeamObs) => p().SOV())
      .GroupBy((p: TeamObs) => p().SOV())
      .ToArray()[0].source;
  }

  WorstSOV(teams: TeamObs[]): TeamObs[] {
    return Enumerable.From(teams)
      .OrderBy((p: TeamObs) => p().SOV())
      .GroupBy((p: TeamObs) => p().SOV())
      .ToArray()[0].source;
  }

  BestSOS(teams: TeamObs[]): TeamObs[] {
    return Enumerable.From(teams)
      .OrderByDescending((p: TeamObs) => p().SOS())
      .GroupBy((p: TeamObs) => p().SOS())
      .ToArray()[0].source;
  }

  WorstSOS(teams: TeamObs[]): TeamObs[] {
    if (teams.length) {
      return Enumerable.From(teams)
        .OrderBy((p: TeamObs) => p().SOS())
        .GroupBy((p: TeamObs) => p().SOS())
        .ToArray()[0].source;
    }
    return [];
  }

  GetPct(teams: TeamObs[]): any[] {
    return Enumerable.From(teams)
      .OrderByDescending((p: TeamObs) => p().GameRecords.AllRecord.Pct())
      .GroupBy((p: TeamObs) => p().GameRecords.AllRecord.Pct())
      .ToArray();
  }

  GetDivPct(teams: TeamObs[]): any[] {
    return Enumerable.From(teams)
      .OrderByDescending((p: TeamObs) => p().GameRecords.DivisionRecord.Pct())
      .GroupBy((p: TeamObs) => p().GameRecords.DivisionRecord.Pct())
      .ToArray();
  }

  GetConfPct(teams: TeamObs[]): any[] {
    return Enumerable.From(teams)
      .OrderByDescending((p: TeamObs) => p().GameRecords.ConferenceRecord.Pct())
      .GroupBy((p: TeamObs) => p().GameRecords.ConferenceRecord.Pct())
      .ToArray();
  }

  GetConfPctNaN(teams: TeamObs[]): any[] {
    return Enumerable.From(teams)
      .OrderByDescending((p: TeamObs) => p().GameRecords.ConferenceRecord.PctNaN())
      .GroupBy((p: TeamObs) => p().GameRecords.ConferenceRecord.PctNaN())
      .ToArray();
  }

  CalcPct(WLTArray: number[]): number {
    const denominator = WLTArray[0] + WLTArray[1] + WLTArray[2];
    if (denominator === 0) {
      return 0;
    }
    return (WLTArray[0] + 0.5 * WLTArray[2]) / denominator;
  }

  CalcPctNaN(WLTArray: number[]): number {
    const denominator = WLTArray[0] + WLTArray[1] + WLTArray[2];
    if (denominator === 0) {
      return -1;
    }
    return (WLTArray[0] + 0.5 * WLTArray[2]) / denominator;
  }

  CalcGamesBack(topWins: number, topLosses: number, teamWins: number, teamLosses: number): number {
    return ((topWins - teamWins) + (teamLosses - topLosses)) / 2;
  }

  // ─── Permalink ────────────────────────────────────────────────────────────

  permalink(message: string): void {
    this.updateSaveString();
    prompt(message, this.UrlRoute());
    // window.ga("send", "event", "Permalink", "Generated");
  }

  // ─── PrimeTime conversion (instance methods — decoupled from global vm) ───

  /**
   * Returns the short description for a primeTimeNum using this.PrimeTimeList.
   * Replaces the former static method that referenced the global vm.
   */
  convertPrimeTimeToShortString(primeTimeNum?: number): string {
    if (primeTimeNum != null) {
      const gameTypes: GameType[] = Enumerable.From(this.PrimeTimeList())
        .Where((p: GameType) => p.GameTypeID === primeTimeNum)
        .ToArray();
      if (gameTypes.length === 1) {
        return gameTypes[0].ShortDesc;
      }
    }
    return '';
  }

  /**
   * Returns the long description for a primeTimeNum using this.PrimeTimeList.
   * Replaces the former static method that referenced the global vm.
   */
  convertPrimeTimeToString(primeTimeNum?: number): string {
    if (primeTimeNum != null) {
      const gameTypes: GameType[] = Enumerable.From(this.PrimeTimeList())
        .Where((p: GameType) => p.GameTypeID === primeTimeNum)
        .ToArray();
      if (gameTypes.length === 1) {
        return gameTypes[0].LongDesc;
      }
    }
    return '';
  }

  /**
   * Returns "Long prime time desc<br/>game date display" for a game.
   * Replaces the former static method that referenced the global vm.
   * Calls gameDateLongDisplay() on the game instance (available on GameBase).
   */
  convertPrimeTimeAndDateToString(game: IGame): string {
    const dateDisplay = (game as any).gameDateLongDisplay?.() ?? '';
    return this.convertPrimeTimeToString(game.primeTimeNum()) + '<br/>' + dateDisplay;
  }

  // ─── Static utility methods ───────────────────────────────────────────────

  static getQueryVariable(variable: string): string | null {
    const query = window.location.search.substring(1);
    const vars = query.split('&');
    for (let i = 0; i < vars.length; i++) {
      const pair = vars[i].split('=');
      if (pair[0].toUpperCase() === variable.toUpperCase()) {
        return pair[1];
      }
    }
    return null;
  }

  static queryVariableToDictionary(variable: string): Record<string, unknown> & { overrides: boolean } {
    const dict: Record<string, unknown> & { overrides: boolean } = { overrides: false };
    const query = SchedulePickerBase.getQueryVariable('c');
    if (query) {
      const split = query.split('|');
      for (const item of split) {
        const split2 = item.split(':');
        dict[split2[0]] = split2[1];
      }
      dict.overrides = true;
    }
    return dict;
  }

  // ─── Save / History ───────────────────────────────────────────────────────

  pushHistory(): void {
    if (this.config.draftOrderPage) {
      return;
    }
    const self = this;
    clearTimeout(this.compressCallback);
    // Compressing the URL is expensive — debounce 500ms after last pick.
    this.compressCallback = setTimeout(() => {
      if (!self.BlockHistoryPush()) {
        const lzString = self.SaveString();
        localStorage.setItem(self.config.leagueGuid, lzString);
        if (self.config.sportId !== undefined) {
          schedulePickPutDebounced(self.config.sportId, self.config.leagueGuid, lzString);
        }
        if (history.pushState) {
          let teamAbbreviation: string | undefined;
          if (window.location.hash) {
            teamAbbreviation = window.location.hash.slice(1);
          }
          const isDebugMode = userConfigManager.UserConfig.IsDebugMode;
          let newurl =
            window.location.protocol +
            '//' +
            window.location.host +
            window.location.pathname +
            '?L=' +
            lzString +
            (isDebugMode ? '&dfpkey=testads' : '');
          if (teamAbbreviation) {
            newurl += `#${teamAbbreviation}`;
          }
          window.history.pushState({ path: newurl }, '', newurl);
        }
      }
      self.finishComputing();
    }, 500);
  }

  updateSaveString(): void {
    const saveString = LZString.compressToEncodedURIComponent(this.GameArray.join(''));
    if (this.SaveString() !== saveString) {
      this.SaveString(saveString);
    }
  }

  setCurrentSeason(reset: boolean): void {
    const self = this;
    if (!self.IsComputing()) {
      if (!self.config.draftOrderPage) {
        this.startComputing();
      }
      setTimeout(() => {
        if (reset) {
          const sportId = self.config.sportId;
          if (sportId == null) {
            self.finishComputing();
            return;
          }
          ppFetch<{ saveString: string }>({
            url: `${getApimBaseUrl()}/LeagueEngine/save-strings/${sportId}/${self.config.leagueGuid}`,
            method: 'GET',
          })
            .then((result) => {
              self.CurrentSeasonString(result?.saveString ?? '');
            })
            .catch((err) => {
              console.error('GetSaveString failed', err);
            })
            .finally(() => {
              setTimeout(() => {
                self.UrlString(self.CurrentSeasonString());
                self.UrlString.valueHasMutated();
                self.switchContext({
                  key: self.BiggestWeek(),
                  value: self.Weeks[self.BiggestWeek()],
                });
                self.finishComputing();
              }, 100);
            });
        } else {
          self.switchContext({ key: 1, value: self.Weeks[1] });
          self.CurrentSeasonString(self.EmptySeasonString);
          self.pushHistory();
        }
      }, 100);
    }
  }

  // ─── Team stats ───────────────────────────────────────────────────────────

  showTeamStats(data: ITeam): void {
    const self = this;
    self.SelectedTeam(data);
    self.SelectedTeam().Schedule(self.getScheduleForTeam(self.SelectedTeam()));
    self.SelectedTeam().Schedule.valueHasMutated();
    let hash = '#';
    if (self.SelectedTeam().ProgramName) {
      hash += `Team_${self.SelectedTeam().TeamID}`;
    } else {
      hash += self.SelectedTeam().TeamAbbreviation;
    }
    self.Hash(hash);
    self.IsTeamSelected(true);
  }

  getScheduleForTeam(team: ITeam): Array<KnockoutObservable<IGame>> {
    const schedule = new Array<KnockoutObservable<IGame>>();
    for (const weekInd in this.Weeks) {
      const week = this.Weeks[weekInd];
      let foundGame = false;
      for (const gameModeInd in week) {
        const games: KnockoutObservable<IGame>[] = (week as unknown as Record<string, IWeek>)[
          gameModeInd
        ].GamesComputed();
        const gamesTeamIsIn = ko.utils.arrayFilter(
          games,
          (item: KnockoutObservable<IGame>) =>
            item().away &&
            item().home &&
            (item().away().TeamID === team.TeamID || item().home().TeamID === team.TeamID),
        );
        if (gamesTeamIsIn.length > 0) {
          ko.utils.arrayPushAll(schedule, gamesTeamIsIn);
          foundGame = true;
        }
      }
      if (!foundGame) {
        schedule.push(null as unknown as KnockoutObservable<IGame>);
      }
    }
    return schedule;
  }

  getGamesVsEachOther(
    team1: TeamObs,
    team2: TeamObs,
  ): KnockoutObservable<IGame>[] {
    if (team1 && team2) {
      const schedule = this.getScheduleForTeam(team1());
      const teamname = team1().TeamAbbreviation;
      const team2Name = team2().TeamAbbreviation;
      return Enumerable.From(schedule)
        .Where((p: KnockoutObservable<IGame>) => {
          if (p) {
            return p() != null && p().BothTeamNamesInGame(teamname, team2Name);
          }
          return false;
        })
        .ToArray();
    }
    return [];
  }

  destroy(): void {
    const contentEl = document.getElementById('content');
    if (contentEl) {
      ko.cleanNode(contentEl);
      contentEl.innerHTML = '';
    }
  }

  // ─── Watchlist CRUD ───────────────────────────────────────────────────────

  private getWatchlistSportId(): number {
    if (this.config.sportId && Number.isFinite(this.config.sportId)) {
      return this.config.sportId;
    }

    const sportIds: Record<string, number> = {
      football: 1,
      basketball: 2,
      baseball: 3,
      hockey: 8,
    };

    const sportId = sportIds[this.PredictorSport.toLowerCase()];
    if (!sportId) {
      throw new Error(`Unable to resolve sport id for ${this.PredictorSport}`);
    }
    return sportId;
  }

  private getWatchlistApiUrl(path: string): string {
    return `${getApimBaseUrl()}/LeagueEngine/user/${path}`;
  }

  private getCurrentWatchlistSaveString(): string {
    return localStorage.getItem(this.config.leagueGuid) ?? this.SaveString() ?? '';
  }

  private async loadWatchlistFromLeagueEngine(watchlistGuid: string): Promise<Watchlist | null> {
    const data = await apiFetch<ILeagueEngineWatchlistData | undefined>({
      url: this.getWatchlistApiUrl(`watchlist/${encodeURIComponent(watchlistGuid)}`),
      method: 'GET',
    });

    return data ? new Watchlist(fromLeagueEngineWatchlist(data)) : null;
  }

  deleteWatchlist(watchlist: Watchlist): void {
    const self = this;
    if (confirm('Are you sure you want to delete this scenario?')) {
      apiFetch<void>({
        method: 'DELETE',
        url: self.getWatchlistApiUrl(`watchlist/${self.getWatchlistSportId()}/${encodeURIComponent(watchlist.WatchlistGUID)}`),
      }).then(() => {
        const watchlistToDelete = Enumerable.From(self.Watchlists()).First(
          (s: KnockoutObservable<Watchlist>) => s().WatchlistGUID === watchlist.WatchlistGUID,
        );
        self.Watchlists.remove(watchlistToDelete);
        toastSuccess('Your scenario has been deleted');
      }).catch(() => toastError('Unable to delete your saved scenario'));
    }
  }

  editWatchlist(watchlist: Watchlist): void {
    const self = this;

    self.loadWatchlistFromLeagueEngine(watchlist.WatchlistGUID)
      .then((engineWatchlist) => {
        const wl = engineWatchlist ?? new Watchlist(fromLeagueEngineWatchlist(toLeagueEngineWatchlist(watchlist)));
        wl.SaveString(self.getCurrentWatchlistSaveString() || wl.SaveString());
        self.SelectedWatchlist(wl);

        const modal = openModal(WATCHLIST_MODAL_HTML, {
          title: 'Edit Scenario',
          buttons: {
            save: {
              label: 'Save',
              callback: () => {
                self.SelectedWatchlist().WatchlistName.valueHasMutated();
                if ((self.SelectedWatchlist().WatchlistName as any).isValid?.()) {
                  self.IsEditing(true);
                  self.saveWatchlist();
                  return true;
                }
                return false;
              },
            },
            cancel: {
              label: 'Cancel',
              callback: () => {
                self.IsEditing(false);
                self.SelectedWatchlist(null as unknown as Watchlist);
              },
            },
          },
        });
        ko.applyBindings(self, modal);
      })
      .catch(() => toastError('Unable to load this saved scenario'));
  }

  createWatchlist(): void {
    const self = this;

    const wl = new Watchlist(null);
    wl.LeagueGUID(self.config.leagueGuid);
    wl.SaveString(self.getCurrentWatchlistSaveString());
    wl.Version = 2;
    self.SelectedWatchlist(wl);

    const modal = openModal(WATCHLIST_MODAL_HTML, {
      title: 'Save Scenario',
      buttons: {
        save: {
          label: 'Save',
          callback: () => {
            self.SelectedWatchlist().WatchlistName.valueHasMutated();
            if ((self.SelectedWatchlist().WatchlistName as any).isValid?.()) {
              self.saveWatchlist();
              return true;
            }
            return false;
          },
        },
        cancel: {
          label: 'Cancel',
          callback: () => {
            self.SelectedWatchlist(null as unknown as Watchlist);
          },
        },
      },
    });
    ko.applyBindings(self, modal);
  }

  saveWatchlist(): void {
    const self = this;
    const wl = self.SelectedWatchlist();
    apiFetch<ILeagueEngineWatchlistData>({
      url: self.getWatchlistApiUrl(`watchlist/${self.getWatchlistSportId()}`),
      data: toLeagueEngineWatchlist(wl),
      method: 'POST',
    })
      .then((saved) => {
        const savedWatchlist = new Watchlist(fromLeagueEngineWatchlist(saved));
        if (self.IsEditing()) {
          const toReplace = Enumerable.From(self.Watchlists()).First(
            (p: KnockoutObservable<Watchlist>) =>
              p().WatchlistGUID === savedWatchlist.WatchlistGUID,
          );
          toReplace(savedWatchlist);
          toReplace.valueHasMutated();
          self.Watchlists.valueHasMutated();
        } else {
          self.Watchlists.push(ko.observable(savedWatchlist));
        }
        toastSuccess('Your scenario has been saved');
      })
      .catch(() => toastError('Unable to save your scenario'))
      .finally(() => {
        self.IsEditing(false);
      });
  }

  // ─── Game schedule numbers ────────────────────────────────────────────────

  /**
   * Assigns sequential gameScheduleNum values to games that don't have one yet,
   * and rebuilds the GameArray to match the new game range.
   * Ported exactly from V1 SchedulePickerBase.setGameScheduleNumbers().
   */
  setGameScheduleNumbers(gameRange: number): void {
    const self = this;
    let position = 0;
    if (self.GameArray.length < gameRange) {
      const oldArray = self.GameArray;
      self.GameArray = [];
      for (const weekInd in self.Weeks) {
        const weekRecord = self.Weeks[weekInd];
        ko.utils.arrayForEach(weekRecord, (week: IWeek) => {
          ko.utils.arrayForEach(week.GamesComputed(), (game: KnockoutObservable<IGame>) => {
            const _game = game();
            if (_game.gameScheduleNum() == null && position < gameRange) {
              _game.gameScheduleNum(position);
            }
            self.GameArray.push(oldArray[position] || 0);
            position++;
          });
        });
      }
    }
  }

  // ─── Computing state ──────────────────────────────────────────────────────

  startComputing(): void {
    if (!this.IsTeamSelected()) {
      showSpinner();
      const thinkingEl = document.querySelector<HTMLElement>('#computing .thinking');
      const doneEl = document.querySelector<HTMLElement>('#computing .done');
      if (thinkingEl) thinkingEl.style.display = '';
      if (doneEl) doneEl.style.display = 'none';
      this.IsComputing(true);
    }
  }

  finishComputing(): void {
    hideSpinner();
    document.querySelectorAll<HTMLElement>('#content .hidden').forEach((el) => {
      el.classList.remove('hidden');
    });
    const thinkingEl = document.querySelector<HTMLElement>('#computing .thinking');
    const doneEl = document.querySelector<HTMLElement>('#computing .done');
    if (thinkingEl) thinkingEl.style.display = 'none';
    if (doneEl) doneEl.style.display = '';
    this.IsComputing(false);
  }

  // ─── Watchlist link ───────────────────────────────────────────────────────

  viewWatchlistLink(wl: Watchlist): void {
    prompt(
      'This link will share a timestamped and named version of this scenario',
      `${window.location.protocol}//${window.location.host}/${this.PredictorSport}/CustomLeague/ViewWatchlist/${wl.WatchlistGUID}`,
    );
  }
}
